/**
 * Native Interactive Site Map (ISM) Feature Entry Point.
 *
 * Responsibilities:
 * - Inject Redux slices into the store
 * - Create and bootstrap the plugin host
 * - Manage map library loading and plugin lifecycle
 * - Export Redux actions and selectors for use in components
 */

import { epic$, store, store$ } from '@redux/store';
import { ReplaySubject, fromEvent } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import LoadingStatus from 'enums/loadingStatus';
import { selectLoading, selectConfigurationResourceDomain, selectMapLibraryStatus } from 'layout/redux/selectors';
import createMapLibraryPlugin from 'libraries/mapLibrary.plugin';
import uiStateSlice from 'layout/redux/uiStateSlice';
import modalService from 'layout/services/modalService';
import drawerService from 'layout/services/drawerService';
import createInteractiveSiteMapModalHandler from './handlers/modalHandler';
import { createMapLibraryLifecycleHandlers, getMapSourceUrl } from './handlers/mapLibraryLifecycleHandlers';
import createDrawerLifecycleHandlers from './handlers/drawerLifecycleHandlers';
import createMapControlsPlugin from './plugins/mapTypeToggle.plugin';
import createInteractiveSiteMapDrawerPlugin, { DRAWER_ID } from './plugins/drawer.plugin';
import createMapPlugin from './plugins/map.plugin';
import createPluginHost from './plugins/app';
import createRenderCommunitySiteMapPlugin from './plugins/renderCommunitySiteMap.plugin';
import createSiteMapViewportPlugin from './plugins/siteMapViewport.plugin';
import createListViewPlugin from './plugins/listView.plugin';
import { createNativeInteractiveSiteMapDataSlice, DATA_SLICE_KEY } from './redux/dataSlice';

/**
 * Creates the modal and orchestrates Interactive Site Map initialization
 */
export default function setupNativeInteractiveSiteMap({ communityId, latitude, longitude, mapLibrarySlice } = {}) {
  const appRoot = document.querySelector('[data-native-interactive-site-map]');
  const triggerButton = document.querySelector('[data-show-native-interactive-site-map]');

  if (!appRoot && !triggerButton) {
    return;
  }

  // Inject Redux slices
  const dataSlice = createNativeInteractiveSiteMapDataSlice();
  store.injectReducer(DATA_SLICE_KEY, dataSlice.reducer);
  
  // A ReplaySubject(1) that emits once when runtime configuration becomes available.
  const configuration$ = new ReplaySubject(1);
  store$.pipe(
    filter(state => selectLoading(state) === LoadingStatus.Succeeded.toString()),
    map(state => ({
      resourceDomain: selectConfigurationResourceDomain(state),
    })),
    take(1),
  ).subscribe(configuration$);

  // A ReplaySubject(1) that emits once when the map library has loaded successfully.
  const mapLibraryLoaded$ = new ReplaySubject(1);
  store$.pipe(
    map(selectMapLibraryStatus),
    filter(status => status === LoadingStatus.Succeeded.toString()),
    take(1),
  ).subscribe(mapLibraryLoaded$);

  const mapSourceElement = document.querySelector('[data-maps-script]');
  const mapSource = getMapSourceUrl(mapSourceElement);

  const mapLibraryLifecycle = createMapLibraryLifecycleHandlers(
    store.dispatch.bind(store),
    mapLibrarySlice.actions,
  );

  const drawerLifecycle = createDrawerLifecycleHandlers(
    store.dispatch.bind(store),
    uiStateSlice.actions,
    DRAWER_ID
  );

  let host = null;
  let beforeUnloadHandler = null;

  async function stopHost() {
    if (!host || typeof host.stop !== 'function') {
      return;
    }

    if (beforeUnloadHandler) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      beforeUnloadHandler = null;
    }

    await host.stop();
    host = null;
  }

  async function bootstrapNativeInteractiveSiteMap(rootElement) {
    const drawerPlugin = triggerButton && createInteractiveSiteMapDrawerPlugin();

    host = createPluginHost({
      root: rootElement,
      services: {
        epic$,
        store$,
        dispatch: store.dispatch.bind(store),
        getState: store.getState.bind(store),
        configuration$,
        drawerService,
        dataActions: dataSlice.actions,
        mapLibraryActions: mapLibrarySlice.actions,
        mapLibraryLoaded$,
      },
      state: {
        community: { communityId, latitude, longitude },
      },
      plugins: [
        createMapLibraryPlugin({
          provider: 'google',
          config: {
            src: mapSource,
            onScriptInjected: mapLibraryLifecycle.onMapLibraryFetch,
            onLoad: mapLibraryLifecycle.onMapLibraryLoaded,
            onError: mapLibraryLifecycle.onMapLibraryError,
          },
        }),
        createMapPlugin(),
        createMapControlsPlugin(),
        createRenderCommunitySiteMapPlugin(),
        createSiteMapViewportPlugin(),
        createListViewPlugin(),
        drawerPlugin,
      ].filter(Boolean),
    });

    await host.init();
    await host.start();

    beforeUnloadHandler = () => stopHost();
    window.addEventListener('beforeunload', beforeUnloadHandler);
  }

  function createInteractiveSiteMapModal() {
    const modalTemplate = document.querySelector('[data-native-interactive-site-map-modal-template]');

    if (host || !modalTemplate) {
      return;
    }

    createInteractiveSiteMapModalHandler({
      appRoot: modalTemplate,
      modalService,
      dispatch: store.dispatch.bind(store),
      modalAction: uiStateSlice.actions.modalInformation,
      onReady: drawerLifecycle.onDrawerReady,
      onBootstrap: bootstrapNativeInteractiveSiteMap,
      onStop: async () => {
        drawerLifecycle.onDrawerClosed();
        await stopHost();
      },
    });
  }

  if (triggerButton) {
    fromEvent(triggerButton, 'click').subscribe(createInteractiveSiteMapModal);
    return;
  }

  (async () => {
    await bootstrapNativeInteractiveSiteMap(appRoot);
  })();
}
